import { useState } from 'react';
import { Eye } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { EstimateStatusBadge } from '@/components/estimates/EstimateStatusBadge';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { isDateOnlyPast } from '@/utils/locale';
import { cn } from '@/lib/utils';
import type { EstimateWithRelations } from '@/types/estimate';
import { EstimateQuickViewSheet } from '@/components/estimates/EstimateQuickViewSheet';

interface CustomerEstimatesTableProps {
  estimates: EstimateWithRelations[];
  isLoading?: boolean;
}

export function CustomerEstimatesTable({ estimates, isLoading }: CustomerEstimatesTableProps) {
  // Open the estimate here rather than navigating to the estimates list: the
  // quick view carries every action (send, accept/decline, cancel, delete).
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
  const { formatMoney, formatShortDate } = useCompanyLocale();

  if (isLoading) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">Loading estimates...</p>
      </div>
    );
  }

  if (estimates.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">No estimates for this customer yet.</p>
      </div>
    );
  }

  const accepted = estimates.filter((e) => e.status === 'accepted');
  const openQuotes = estimates.filter((e) => ['draft', 'sent', 'viewed'].includes(e.status));
  const acceptedValue = accepted.reduce((sum, e) => sum + Number(e.estimate_amount || 0), 0);
  const openValue = openQuotes.reduce((sum, e) => sum + Number(e.estimate_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border p-4">
          <p className="text-xs font-medium text-muted-foreground">Quoted</p>
          <p className="text-2xl font-semibold tabular-nums">{estimates.length}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs font-medium text-muted-foreground">Accepted</p>
          <p className="text-2xl font-semibold tabular-nums text-success">{formatMoney(acceptedValue)}</p>
          <p className="text-xs text-muted-foreground">{accepted.length} of {estimates.length}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs font-medium text-muted-foreground">Awaiting answer</p>
          <p className="text-2xl font-semibold tabular-nums text-info">{formatMoney(openValue)}</p>
          <p className="text-xs text-muted-foreground">{openQuotes.length} open</p>
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estimate</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Valid until</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {estimates.map((estimate) => {
              const expired = isDateOnlyPast(estimate.valid_until) && !['accepted', 'declined', 'cancelled'].includes(estimate.status);
              return (
                <TableRow
                  key={estimate.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedEstimateId(estimate.id)}
                >
                  <TableCell className="font-medium">{estimate.estimate_number}</TableCell>
                  <TableCell className="text-muted-foreground">{estimate.lead?.lead_number ?? '—'}</TableCell>
                  <TableCell>{formatShortDate(estimate.created_at)}</TableCell>
                  <TableCell className={cn(expired && 'text-destructive')}>
                    {estimate.valid_until ? formatShortDate(estimate.valid_until) : '—'}
                  </TableCell>
                  <TableCell><EstimateStatusBadge status={estimate.status} /></TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{formatMoney(estimate.estimate_amount)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedEstimateId(estimate.id)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <EstimateQuickViewSheet
        estimateId={selectedEstimateId}
        open={!!selectedEstimateId}
        onOpenChange={(next) => !next && setSelectedEstimateId(null)}
      />
    </div>
  );
}
